import React, { FC, memo, MutableRefObject, ReactElement, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { PopperPlacementType } from '@material-ui/core'
import { HelpTagTitles, Tags } from '../../API/Contentful/Constants'
import { getHelpEntries } from '../../API/Contentful/ContentfulAPI'
import { AssetEntryMap, DisplayType, HelpAssets, HelpEntry } from '../../API/Contentful/Types'
import { useFeatureFlag } from '../../Hooks/useFeatureFlag'
import { ReactState } from '../../Types/Types'
import { HelpEntryModal } from '../HelpEntryModal/HelpEntryModal'
import { HelpEntryTooltip } from '../HelpEntryTooltip/HelpEntryTooltip'
import { IconSize } from '../../../../NonReact/constants/React/Icon/Constants'

type Props = {
    className?: string
    helpContentTitle: HelpTagTitles | string
    tag: Tags
    buttonLabel?: string
    position?: PopperPlacementType
    iconSize?: IconSize
}

/**
 * Display a help question mark to the user.
 *
 * @param helpContentTitle - The title of the help content to display.
 * @param tag - The tag to use to get the help content.
 * @param position - The position of the tooltip.
 * @param className - The class name to apply to the component.
 * @param buttonLabel - The label to display on the button.
 *
 * @returns - The help question mark.
 */
export const HelpTag: FC<Props> = memo(
    ({ className, helpContentTitle, tag, position, buttonLabel, iconSize }: Props): ReactElement => {
        // There is no need to display an error to the customer in this case, but it is useful to log it.
        const { error }: Console = console
        const [assets, setAssets]: ReactState<Definable<AssetEntryMap>> = useState<Definable<AssetEntryMap>>()
        const [helpEntry, setHelpEntry]: ReactState<Definable<HelpEntry>> = useState<Definable<HelpEntry>>()

        const showHelpTags: Definable<boolean> = useFeatureFlag('showInProductEducation')

        const isMounted: MutableRefObject<boolean> = useIsMountedRef()

        useEffect((): (() => void) => {
            // Get the help entries from the server.
            getHelpEntries(tag)
                .then((helpAssets: Definable<HelpAssets>): void => {
                    if (isMounted.current && helpAssets) {
                        setAssets(helpAssets.assets)
                        setHelpEntry(helpAssets?.helpEntries?.[helpContentTitle])
                    }
                })
                .catch(error)

            return (): void => {
                // This is going to be everywhere so it is worth making sure it is cleaned up.
                setHelpEntry(undefined)
            }
        }, [])

        return helpEntry && showHelpTags ? (
            <>
                {helpEntry?.displayType === DisplayType.MODAL ? (
                    <HelpEntryModal
                        className={className}
                        helpEntry={helpEntry}
                        assets={assets}
                        buttonLabel={buttonLabel}
                        iconSize={iconSize}
                    />
                ) : (
                    <></>
                )}
                {helpEntry?.displayType === DisplayType.TOOLTIP ? (
                    <HelpEntryTooltip className={className} helpEntry={helpEntry} assets={assets} position={position} />
                ) : (
                    <></>
                )}
            </>
        ) : (
            <></>
        )
    }
)
